(ns hai.ui.components.collection.table
  "Data table that supports pagination, sorting, searching, filtering and bulk actions via Pathom
  and Datomic. See `component` docstring for more details."
  (:require
   [hai.entity.helpers :as entity]
   [hai.ui.components :as c]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.core :as core]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.components.collection.pagination :as pagination]
   [hai.ui.components.collection.query :as query]
   [hai.ui.components.collection.search :as search]
   [hai.ui.components.collection.settings :as settings]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.kanban :as kanban]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.form.core :as form]
   [hai.ui.rf-sugar :refer [<s >e >e!]]
   [hai.ui.utils.validate :refer [validate]]
   [humbleai.belt.malli.core :as m]
   [humbleai.belt.malli.transform.default-value :as default-value]
   [humbleai.belt.malli.transform.strip-extra-keys :as strip-extra-keys]
   [humbleai.belt.malli.transform.translate-keys :as translate-keys]
   [lambdaisland.ornament :as o]
   [malli.transform :as mt]
   [reagent.core :as r]))

(def Field
  [:and
   [:map
    [:table.column/id
     {:optional true
      ::translate-keys/from :id}
     :any]
    [:table.column/header
     {::translate-keys/from :header}
     :any]
    [:table.column/attr
     {:optional true
      ::translate-keys/from :attr}
     :any]
    [:table.column/render
     {:optional true
      ::translate-keys/from :render}
     :any]
    [:table.column/render-with-collection
     {:optional true
      ::translate-keys/from :render-with-collection}
     :any]
    ;; Optional fixed column width (any CSS length, e.g. "7em"). Applied to the
    ;; column's header cell -- see `views.table/header` -- which sizes the column.
    [:table.column/width
     {:optional true
      ::translate-keys/from :width}
     :any]
    [:table.column/filter
     {:optional true
      ::translate-keys/from :filter}
     filters/Filter]
    ;; TODO move to pagination
    [:table.column/sort?
     {::translate-keys/from :sort?
      ::default-value/fn (comp boolean :table.column/attr)}
     :boolean]]
   [:fn
    {:error/message "Column definition must contain either `:render` or `:attr`"}
    (fn [{:table.column/keys [render attr]}]
      (or render attr))]
   [:fn
    {:error/message "Column definition with `:filter` must also contain `:attr`"}
    (fn [{:table.column/keys [filter attr]}]
      (if filter attr true))]])

(def EditableField
  (m/merge
   Field
   [:and
    [:map
     [:table.column/field
      {:optional true
       ::translate-keys/from :field}
      :any]]
    [:fn
     {:error/message "Column definitions with `:field` must contain `:attr`"}
     (fn [{:table.column/keys [field attr]}]
       (or (not field)
           attr))]]))

(def RowsTable
  [:and
   (m/merge
    core/Collection
    [:map
     [:collection/type
      {:default :rows}
      [:= :rows]]
     [:table/id
      {::translate-keys/from :id
       ::default-value/fn (fn [{:as _collection
                                :table/keys [rows-sub rows]
                                :collection.query/keys [query]}]
                            (cond->> (gensym)
                              (or query rows-sub) (str (or query rows-sub) " - ")
                              query (str :collection.query/query " - ")
                              rows-sub (str :table/rows-sub " - ")
                              rows (str :table/rows " - ")))}
      :any]
     [:table/rows
      {:optional true
       ::translate-keys/from :rows}
      [:vector :any]]
     [:table/no-items
      {:optional true
       ::translate-keys/from :no-items}
      ifn?]
     [:table/on-search
      {:optional true
       ::translate-keys/from :on-search}
      ifn?]
     ;; TODO table prop
     [:table/controls
      {:optional true
       ::translate-keys/from :controls}
      ifn?]])
   ;; TODO fix
   [:fn {:error/message "`:table/rows` does not currently support `:table/on-search`"}
    (fn [args]
      (not (and (:table/rows args)
                (:table/on-search args))))]])

(def RowsSubTable
  (m/merge
   core/Collection
   RowsTable
   [:map
    [:collection/type
     {:default :rows-sub}
     [:= :rows-sub]]
    [:table/rows-sub
     {::translate-keys/from :rows-sub}
     [:or :keyword vector?]]]))

(def QueryTable
  (m/merge
   core/Collection
   RowsTable
   query/QueryCollection))

(def StaticCollection
  [:multi
   {:error/message "One of `:rows`, `:query` or `:rows-sub` must be present"
    :dispatch (fn [collection]
                (cond
                  (and (or (:query collection) (:collection.query/query collection))
                       (not (or (:rows-sub collection) (:table/rows-sub collection))))
                  :collection/query

                  (and (or (:rows-sub collection) (:table/rows-sub collection))
                       (not (or (:query collection) (:collection.query/query collection))))
                  :collection/rows-sub

                  (or (:rows collection) (:table/rows collection))
                  :collection/rows))}
   [:collection/query QueryTable]
   [:collection/rows-sub RowsSubTable]
   [:collection/rows RowsTable]])

(def EditableCollection
  (m/merge
   StaticCollection
   [:map
    [:table/schema
     {::translate-keys/from :schema}
     entity/attr-schema?]
    [:table/update-mutation
     {::translate-keys/from :update-mutation}
     :symbol]
    [:table/on-change
     {:optional true
      ::translate-keys/from :on-change}
     ifn?]
    [:collection/editable?
     {:default false
      ::translate-keys/from :editable?}
     :boolean]]))

(def PagedTable
  [:map
   [:table/pagination
    {::translate-keys/from :pagination}
    ;; TODO split this up
    (m/merge
     pagination/Pagination
     pagination/Sort)]
   ;; TODO remove the need for this
   [:table/paginate?
    {:default false
     ::translate-keys/from :paginate?}
    :boolean]
   [:table/pagination-position
    {:default :bottom
     ::translate-keys/from :pagination-position}
    [:enum :top :bottom :both]]])

(def StaticTable
  (m/merge
   [:map
    [:table/columns
     {::translate-keys/from :columns}
     [:vector Field]]
    [:table/fields
     {::translate-keys/from :fields
      ;; TODO required if cards view maybe?
      :optional true}
     [:map-of :keyword Field]]
    [:collection.view.card/render
     {::translate-keys/from :card}
     :any]
    [:collection.view/default
     {::translate-keys/from :default-view
      :default :collection.view/table}
     :any]
    ;; The switchable views this collection offers, in `settings/view-options`
    ;; shape (`{:value :title}`). Declared generically here - like `:card` and
    ;; `:default-view` - so it survives `strip-extra-keys`; a consumer that omits
    ;; it falls back to the default Table/Cards options. A kanban board declares
    ;; `[kanban/view-option]` to offer Kanban alone.
    [:collection/view-options
     {:optional true
      ::translate-keys/from :view-options}
     :any]]
   kanban/KanbanViewConfig
   search/SearchConfig
   StaticCollection
   PagedTable
   bulk-actions/ActionableCollection
   bulk-actions/ExportableCollection))

(def EditableTable
  (m/merge
   [:map
    [:table/columns
     {::translate-keys/from :columns}
     [:vector EditableField]]
    [:table/fields
     {::translate-keys/from :fields
      ;; TODO required if cards view maybe?
      :optional true}
     [:map-of :keyword Field]]
    ;; TODO cards only
    [:collection.view.card/render
     {::translate-keys/from :card}
     :any]
    ;; TODO this is a hack, ask me
    [:collection.view/default
     {::translate-keys/from :default-view
      :default :collection.view/table}
     :any]
    [:collection/view-options
     {:optional true
      ::translate-keys/from :view-options}
     :any]]
   kanban/KanbanViewConfig
   search/SearchConfig
   EditableCollection
   PagedTable
   bulk-actions/ActionableCollection))

(def Table
  [:multi
   {:error/message "Editable tables require both `:schema` and `:update-mutation`"
    :dispatch (fn [table]
                (cond
                  (and (or (:schema table) (:table/schema table))
                       (or (:update-mutation table) (:table/update-mutation table)))
                  :table/editable

                  (not (or (:schema table)
                           (:table/schema table)
                           (:update-mutation table)
                           (:table/update-mutation table)))
                  :table/static))}
   [:table/editable EditableTable]
   [:table/static StaticTable]])

(def transformer
  (mt/transformer
   strip-extra-keys/transformer
   default-value/transformer
   translate-keys/transformer))

(let [decode-table-props* (m/decoder Table transformer)]
  (defn decode-table-props
    [table]
    (->> table
         (decode-table-props*)
         (validate Table))))

(defn- inject-stored-per-page
  "Override the table's per-page with the user's stored per-collection preference
  (`:user-config/collection-settings` → id → :per-page), if any. The preference
  wins over the table's explicit/default page size; with no stored value this is
  a no-op, leaving the explicit size or the malli `default-per-page` (25). Runs
  after `decode-table-props`, so pagination has been normalised to the
  `:table/pagination` key."
  [table]
  (if-let [stored (get-in @(<s [:auth/identity])
                          [:user-config/collection-settings (:table/id table) :per-page])]
    (assoc-in table [:table/pagination :table/per-page] stored)
    table))

(o/defstyled submit-edits-button :div
  ;; TODO fixup docstring
  "Edit toggle component that is also responsible for building the Pathom mutations, a bit inelegant
  but as subscriptions only work in views will do for now. The mutations are constructed by simply
  wrapping the entity in the mutation symbol supplied with the form."
  [:.buttons :flex :gap-3]
  :ml-auto
  ([{:table/keys [id update-mutation] :as table}]
   (when update-mutation
     ;; NOTE the underlying form is setup with the same ID as the table so we use the table ID when
     ;; calling form events.
     (let [valid-value @(<s [:form/valid-value id])
           unsaved? @(<s [:form.field/dirty? id :table/rows])]
       [:<>
        (when unsaved?
          [:div.buttons
           [c/Button
            {:on-click (fn [_]
                         (let [mutations (into []
                                               (map (fn [entity]
                                                      (list update-mutation entity)))
                                               (:table/rows valid-value))]
                           (>e [:resolve mutations
                                {:on-success-fx [[:dispatch [:table/resolve table]]]}])))}
            "Submit"]
           [c/Button
            {:on-click (fn [_] (>e [:form/reset id]))
             :variant "outline"
             :color "crimson"}
            "Cancel"]])]))))

(defn empty-controls
  [& _]
  [:div])

(o/defstyled controls :div
  :flex :flex-row :gap-3 :items-center)

(defn default-controls
  [table]
  [controls
   ;; Text search is hidden when a collection opts out via
   ;; `:collection/searchable? false` (the notes page does this while its filter
   ;; bar is reworked). Flip the flag back to re-enable — nothing else changes.
   (when-not (false? (:collection/searchable? table))
     [search/component table])
   [submit-edits-button table]
   [bulk-actions/dropdown table]
   [bulk-actions/spreadsheet-export table]
   [settings/menu table]])

;; TODO cleanup search-string use form search in on-search

(defn form
  "The editable table form component

  The table form is a form that comprises one field, `:table/rows`, which is a collection field (to
  many, schema `[:vector ...]`) that contains each row in the table. `form-render` contains a single
  collection field component to render the `:table/rows` field, each row itself being a form
  rendered with `row-render` on a single entity form adhering to the passed in `schema`.
  `row-render` iterates over each column rendering either a form field if configured or, if no form
  field is configured in the column definition, the same cell that would be rendered for the table
  in non-edit mode."
  [{:as table
    :table/keys [controls id on-change rows schema]}]
  (let [options (settings/collection-view-options table)
        valid-views (into #{} (map :value) options)
        raw-stored-view (when id
                          (get-in @(<s [:auth/identity]) [:user-config/collection-views id]))
        ;; Only honour a stored view that is still a valid option for this
        ;; collection. A kanban board offers Kanban alone (see
        ;; `settings/collection-view-options`), so a stale `Table`/`Cards` entry
        ;; in `:user-config/collection-views` - saved before the collection was
        ;; pinned to kanban - must not resurface and flip the board off kanban.
        stored-view (when (contains? valid-views raw-stored-view) raw-stored-view)
        ;; The effective default must itself be a view the collection offers. A
        ;; kanban board's decode-time `:collection.view/default` is the schema
        ;; default `:collection.view/table` unless the consumer set `:default-view`,
        ;; which would render a table under a kanban-only enum. Coerce any invalid
        ;; default to the first available option so a board never opens on a view
        ;; it does not offer.
        default-view (or stored-view
                         (let [declared (:collection.view/default table)]
                           (if (contains? valid-views declared)
                             declared
                             (:value (first options)))))
        stored-settings (when id
                          (get-in @(<s [:auth/identity]) [:user-config/collection-settings id]))
        table (cond-> (assoc table :collection.view/default default-view)
                stored-settings (assoc :collection/stored-settings stored-settings))]
    (r/with-let [form-render
                 (fn form-render
                   [form]
                   (let [table (assoc table :table/form form)
                         view (-> form
                                  :form/current-value
                                  :table/settings
                                  :collection/view)]
                     [:<>
                      (if controls
                        [controls table]
                        [default-controls table])
                      (case view
                        :collection.view/table [table/view table]
                        :collection.view/cards [cards/view table]
                        :collection.view/kanban [kanban/view table]
                        [table/view table])]))

                 schema
                 (-> [:map
                      (cond-> {:form/render form-render}
                              ;; TODO Ideally this should be on the `:table/rows` attr but due to an
                              ;; issue/bug with sub-forms the on-change does not fire (see `::setup` in
                              ;; `hai.ui.components.form.core`)
                        on-change (assoc :form/on-change (fn [{:table/keys [rows]} form]
                                                           (try
                                                             (on-change rows form)
                                                             (catch :default _
                                                               (on-change rows))))))

                      [:table/rows
                       {:form.field.collection/field form/component}
                       [:vector
                        ;; Non-editable tables don't require schema
                        (or schema :any)]]]
                     (settings/process-schema table)
                     (search/process-schema table)
                     (filters/process-schema table))

                 form
                 {:form/id            id
                  :form/initial-value {:table/rows rows}
                  :form/schema        schema}]
      [form/component form])))

(defn rows-table*
  [table]
  (>e! [::core/setup table])
  (fn [table]
    [form table]))

(defn query-table*
  [table]
  (>e! [::core/setup table])
  (>e [:table/resolve table])
  (fn [table]
    ;; TODO whether rows is passed in as an arg or we always use the subscription is an open
    ;; question? This was initially done to stop the table from loading without rows.
    (let [rows @(<s [:table/rows table])]
      [form (assoc table :table/rows rows)])))

(o/defstyled component :div
  "Datatable component that supports searching and editable cells

  Props:
  - `id` An optional ID, needed for using the tables external API.
  - `columns` A sequence of column definitions, see Column definition.
  - `schema` A Malli `:map` (attr) schema for the row entity.
  - `update-mutation` A Pathom mutation symbol used for edited entities.
  - `on-search` An optional callback that is passed the search string. If using `query` a default is
    created and that should be used in most circumstances.

  Data sources:
  - `rows` Explicit static rows data, does not currently support search.
  - `rows-sub` A ReFrame subscription with rows data setup outside the table.
  - `query` An EQL query that will resolve to rows data.

  Column definition:
  - `header` A string header title. Required.
  - `render` A hiccup component that takes the row as it's prop. Optional if `attr` is present.
  - `attr` The attribute on the row. Optional if `render` is present.
  - `field` A form field that takes a field definition (see forms). Requires `attr` to be present."
  :flex :flex-col :gap-3
  [:th {:color c/--gray-10
        :line-height c/--line-height-5}]
  [:.buttons :flex :flex-row :gap-3]
  [:.rt-TableHeader {c/--table-row-background-color c/--color-background}]
  [:.rt-TableRow:hover {c/--table-row-background-color c/--accent-2}]
  ["tr:nth-child(2n)" {c/--table-row-background-color c/--gray-2}]
  [:.rt-TableRow.dirty.valid {"--table-row-box-shadow" "inset 0 -1px var(--grass-6)"
                              "--table-row-background-color" "var(--grass-2)"}]
  [:.rt-TableRow.dirty.invalid {"--table-row-box-shadow" "inset 0 -1px var(--crimson-6)"
                                "--table-row-background-color" "var(--crimson-2)"}]
  ([table]
   (let [table (-> table
                   (assoc :collection/app-db-schema Table)
                   (decode-table-props)
                   inject-stored-per-page)]
     (case (:collection/type table)
       :query [query-table* table]
       :rows (when-let [rows (:table/rows table)]
               ^{:key (hash rows)}
               [rows-table* table])
       :rows-sub (when-let [rows @(<s (:table/rows-sub table))]
                   ^{:key (hash rows)}
                   [rows-table* (assoc table :table/rows rows)])
       nil))))
